import { Guid } from '@tableau/guid';

// All Qt interop objects derive from the base WebEngine interop which
// exposes the following interface.
export interface BaseQtInteropAPI {
  GetInteropConfig(callback: (config: InteropConfig) => void): void;
  ExceptionThrown: QtAsyncCallResponse<string>;
}

export interface InteropConfig {
  EnqueueCalls: boolean;
}

export class InvalidAPIError extends Error {
  public constructor(msg: string) {
    super('Invalid API Error: ' + msg);
  }
}

export class NativeError extends Error {
  public handledNatively: boolean = true;
}

/* tslint:disable: no-any */

function retrieveSlot(qtAPI: BaseQtInteropAPI, slotName: string): Function {
  const result = (qtAPI as any)[slotName];
  if (result === undefined) {
    throw new InvalidAPIError(`Required slot '${slotName}' missing from API`);
  } else if (typeof result !== 'function') {
    throw new InvalidAPIError(`Required slot '${slotName}' is not a function`);
  }
  return result;
}

function retrieveSignal(qtAPI: BaseQtInteropAPI, signalName: string): QtAsyncCallResponse<any> {
  const result = (qtAPI as any)[signalName];
  if (result === undefined) {
    throw new InvalidAPIError(`Required signal '${signalName}' missing from API`);
  } else if (typeof result !== 'object' || typeof result.connect !== 'function' || typeof result.disconnect !== 'function') {
    throw new InvalidAPIError(`Required signal '${signalName}' is invalid`);
  }
  return result;
}

function retrieveRequestSlot(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): Function {
  return retrieveSlot(qtAPI, `${qtBaseMethodName}_Request`);
}

function retrieveExceptionSignal(qtAPI: BaseQtInteropAPI): QtAsyncCallResponse<string> {
  return retrieveSignal(qtAPI, 'ExceptionThrown');
}

function createQueuedCall(
  qtAPI: BaseQtInteropAPI,
  qtBaseMethodName: string): Function {
  const requestSlot = retrieveRequestSlot(qtAPI, qtBaseMethodName);
  const returnSignal = retrieveSignal(qtAPI, `${qtBaseMethodName}_Return`);
  const exceptionSignal = retrieveExceptionSignal(qtAPI);

  return (...args: any[]) => new Promise((resolve, reject) => {
    const guidForCall = Guid.create().formattedValue;

    function disconnector(responseGuid: string, v: any): void {
      if (responseGuid === guidForCall) {
        returnSignal.disconnect(disconnector);
        exceptionSignal.disconnect(errorHandler);
        return resolve(v);
      }
    }

    function errorHandler(responseGuid: string, error: string): void {
      if (responseGuid === guidForCall) {
        returnSignal.disconnect(disconnector);
        exceptionSignal.disconnect(errorHandler);
        return reject(new NativeError(error));
      }
    }

    exceptionSignal.connect(errorHandler);
    returnSignal.connect(disconnector);
    requestSlot(guidForCall, ...args);
  });
}

function createDirectCall(
  qtAPI: BaseQtInteropAPI,
  qtBaseMethodName: string): Function {
  const doCall = retrieveSlot(qtAPI, qtBaseMethodName);
  return (...args: any[]) => new Promise<any>(resolve => doCall(...args, resolve));
}

// Ideally this would just be another overload for createQtCall but we really need a compile-time way to switch on the return type to choose
// an implementation - which I don't think exists.
export function createVoidQtCall(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): () => void;
export function createVoidQtCall<A1>(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): (a1: A1) => void;
export function createVoidQtCall<A1, A2>(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): (a1: A1, a2: A2) => void;
export function createVoidQtCall<A1, A2, A3>(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): (a1: A1, a2: A2, a3: A3) => void;
export function createVoidQtCall<A1, A2, A3, A4>(
  qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): (a1: A1, a2: A2, a3: A3, a4: A4) => void;
export function createVoidQtCall(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): Function {
  return (...args: any[]) => {
    new Promise<InteropConfig>(resolve => qtAPI.GetInteropConfig(resolve))
      .then(config => {
        if (config.EnqueueCalls) {
          const ignoredVoidSlotGuid = Guid.EMPTY;
          retrieveRequestSlot(qtAPI, qtBaseMethodName)(ignoredVoidSlotGuid.formattedValue, ...args);
        } else {
          retrieveSlot(qtAPI, qtBaseMethodName)(...args);
        }
      });
  };
}

export function createQtCall<R>(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): () => Promise<R>;
export function createQtCall<R, A1>(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): (a1: A1) => Promise<R>;
export function createQtCall<R, A1, A2>(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): (a1: A1, a2: A2) => Promise<R>;
export function createQtCall<R, A1, A2, A3>(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): (a1: A1, a2: A2, a3: A3) => Promise<R>;
export function createQtCall<R, A1, A2, A3, A4>(
  qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): (a1: A1, a2: A2, a3: A3, a4: A4) => Promise<R>;

export function createQtCall(qtAPI: BaseQtInteropAPI, qtBaseMethodName: string): Function {
  return (...args: any[]) =>
    new Promise<InteropConfig>(resolve => qtAPI.GetInteropConfig(resolve))
      .then(config => {
        if (config.EnqueueCalls) {
          return createQueuedCall(qtAPI, qtBaseMethodName)(...args);
        } else {
          return createDirectCall(qtAPI, qtBaseMethodName)(...args);
        }
      });
}
